import {
    Box,
    Button,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverHeader,
    PopoverBody,
    PopoverCloseButton,
    VStack,
    HStack,
    Text,
    Badge,
    IconButton,
    Divider,
    useColorModeValue,
    Spinner,
    Alert,
    AlertIcon,
} from '@chakra-ui/react'
import { FiBell, FiCheck, FiCheckCircle } from 'react-icons/fi'
import { useNotifications, type Notification } from '../hooks/useNotifications'
import { formatDistanceToNow } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { getNotificationTypeLabel, getNotificationTypeColor } from '../config/notifications'

interface NotificationItemProps {
    notification: Notification
    onMarkAsRead: (id: number) => void
}

function NotificationItem({ notification, onMarkAsRead }: NotificationItemProps) {
    const bgColor = useColorModeValue(
        notification.isRead ? 'gray.50' : 'blue.50',
        notification.isRead ? 'gray.700' : 'blue.900'
    )

    // 使用配置文件中的函數
    const getTypeColor = getNotificationTypeColor
    const getTypeLabel = getNotificationTypeLabel

    return (
        <Box
            p={3}
            bg={bgColor}
            borderRadius="md"
            borderLeft={notification.isRead ? undefined : "4px solid"}
            borderLeftColor={notification.isRead ? undefined : `${getTypeColor(notification.type)}.400`}
            _hover={{ bg: useColorModeValue('gray.100', 'gray.600') }}
            cursor="pointer"
            onClick={() => !notification.isRead && onMarkAsRead(notification.id)}
        >
            <HStack justify="space-between" align="start">
                <VStack align="start" spacing={1} flex={1}>
                    <HStack>
                        <Badge colorScheme={getTypeColor(notification.type)} size="sm">
                            {getTypeLabel(notification.type)}
                        </Badge>
                        {!notification.isRead && (
                            <Box w={2} h={2} bg="blue.400" borderRadius="full" />
                        )}
                    </HStack>

                    <Text fontWeight={notification.isRead ? 'normal' : 'semibold'} fontSize="sm">
                        {notification.title}
                    </Text>

                    <Text fontSize="xs" color="gray.600" noOfLines={2}>
                        {notification.message}
                    </Text>

                    <Text fontSize="xs" color="gray.500">
                        {formatDistanceToNow(new Date(notification.createdAt), {
                            addSuffix: true,
                            locale: zhTW,
                        })}
                    </Text>
                </VStack>

                {!notification.isRead && (
                    <IconButton
                        aria-label="標記為已讀"
                        icon={<FiCheck />}
                        size="xs"
                        variant="ghost"
                        colorScheme="blue"
                        onClick={(e) => {
                            e.stopPropagation()
                            onMarkAsRead(notification.id)
                        }}
                    />
                )}
            </HStack>
        </Box>
    )
}

export default function NotificationCenter() {
    const {
        notifications,
        unreadCount,
        isConnected,
        loading,
        error,
        markAsRead,
        markAllAsRead,
        fetchNotifications,
    } = useNotifications()

    return (
        <Popover placement="bottom-end">
            <PopoverTrigger>
                <Box position="relative">
                    <IconButton
                        aria-label="通知"
                        icon={<FiBell />}
                        variant="ghost"
                        size="md"
                    />
                    {unreadCount > 0 && (
                        <Badge
                            position="absolute"
                            top="-1"
                            right="-1"
                            colorScheme="red"
                            borderRadius="full"
                            fontSize="xs"
                            minW={5}
                            h={5}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </Badge>
                    )}
                    {!isConnected && (
                        <Box
                            position="absolute"
                            bottom="0"
                            right="0"
                            w={3}
                            h={3}
                            bg="red.400"
                            borderRadius="full"
                            border="2px solid white"
                        />
                    )}
                </Box>
            </PopoverTrigger>

            <PopoverContent w="400px" maxH="500px">
                <PopoverHeader>
                    <HStack justify="space-between">
                        <Text fontWeight="semibold">通知中心</Text>
                        <HStack spacing={2}>
                            {!isConnected && (
                                <Badge colorScheme="red" size="sm">離線</Badge>
                            )}
                            {isConnected && (
                                <Badge colorScheme="green" size="sm">即時</Badge>
                            )}
                            {unreadCount > 0 && (
                                <Button
                                    size="xs"
                                    variant="ghost"
                                    leftIcon={<FiCheckCircle />}
                                    onClick={markAllAsRead}
                                >
                                    全部已讀
                                </Button>
                            )}
                        </HStack>
                    </HStack>
                </PopoverHeader>

                <PopoverCloseButton />

                <PopoverBody p={0}>
                    {error && (
                        <Alert status="error" size="sm">
                            <AlertIcon />
                            <Text fontSize="sm">{error}</Text>
                        </Alert>
                    )}

                    {loading ? (
                        <Box p={4} textAlign="center">
                            <Spinner size="sm" />
                            <Text mt={2} fontSize="sm" color="gray.600">
                                載入通知中...
                            </Text>
                        </Box>
                    ) : notifications.length === 0 ? (
                        <Box p={4} textAlign="center">
                            <Text fontSize="sm" color="gray.600">
                                暫無通知
                            </Text>
                        </Box>
                    ) : (
                        <VStack spacing={0} align="stretch" maxH="400px" overflowY="auto">
                            {notifications.map((notification, index) => (
                                <Box key={notification.id}>
                                    <NotificationItem
                                        notification={notification}
                                        onMarkAsRead={markAsRead}
                                    />
                                    {index < notifications.length - 1 && <Divider />}
                                </Box>
                            ))}
                        </VStack>
                    )}

                    {notifications.length > 0 && (
                        <>
                            <Divider />
                            <Box p={3} textAlign="center">
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={fetchNotifications}
                                    isLoading={loading}
                                >
                                    重新載入
                                </Button>
                            </Box>
                        </>
                    )}
                </PopoverBody>
            </PopoverContent>
        </Popover>
    )
}